import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import YearPager from "../lists/common/YearPager";
import SortSelect from "../lists/common/SortSelect";
import StageSelect, { StageOption } from "../lists/common/StageSelect";
import "../../../assets/styles/common/listCard.scss";
import {
  getAverageBySongs,
  getTotalBySongs,
  getSortedGroups,
} from "../../../utils/scoreUtils";
import { GroupData } from "@/types/GroupData";
import ResultTable from "../lists/common/ResultTable";
import OrderModal from "../lists/common/OrderModal";

import ResultPageSettingModal from "../lists/common/ResultPageModal/ResultPageSettingModal";
import { useResultPages } from "../../../hooks/useResultPages";
import { Settings } from "lucide-react";

interface ResultListsProps {
  onEdit: (groupId: string) => void;
  selectedTab: string;
}

const ResultLists: React.FC<ResultListsProps> = ({ selectedTab }) => {
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [currentYear, setCurrentYear] = useState(
    () => Number(localStorage.getItem("currentYear")) || 2024,
  );
  const [sortOption, setSortOption] = useState(
    () => localStorage.getItem("sortOption") || "entryNumber",
  );
  const [editGroupId, setEditGroupId] = useState<string | null>(null);
  const [editableResults, setEditableResults] = useState<{
    [key: string]: string;
  }>({});
  const [editablePenalties, setEditablePenalties] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedStage, setSelectedStage] = useState<StageOption>(
    () => (localStorage.getItem("selectedStage") as StageOption) || "All",
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { resultPages, fetchResultPages } = useResultPages();
  const [resultPageModalOpen, setResultPageModalOpen] = useState(false);
  const [resultPageModalMode, setResultPageModalMode] = useState<
    "create" | "manage"
  >("create");

  useEffect(
    () => localStorage.setItem("currentYear", String(currentYear)),
    [currentYear],
  );
  useEffect(() => localStorage.setItem("sortOption", sortOption), [sortOption]);

  useEffect(() => {
    localStorage.setItem("selectedStage", selectedStage);
  }, [selectedStage]);

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleEdit = (group: GroupData) => {
    const results: { [key: string]: string } = {};
    const penalties: { [key: string]: boolean } = {};
    for (let i = 1; i <= 4; i++) {
      ["Mus", "Per", "Sng"].forEach((type) => {
        const key = `songResult${type}${i}` as keyof GroupData;
        results[`songResult${type}${i}`] = String(group[key] ?? "");
        const penaltyKey = `songPenalty${type}${i}` as keyof GroupData;
        penalties[`songPenalty${type}${i}`] = Boolean(group[penaltyKey]);
      });
    }
    setEditableResults(results);
    setEditablePenalties(penalties);
    setEditGroupId(group.id);
  };

  const fetchGroups = async () => {
    const querySnapshot = await getDocs(collection(db, "groups"));
    const groupList: GroupData[] = querySnapshot.docs
      .map((doc) => ({ id: doc.id, ...(doc.data() as Omit<GroupData, "id">) }))
      .filter((group) => group.isDeleted !== true);
    setGroups(groupList);
  };

  const handleChange = (key: string, value: string) => {
    setEditableResults((prev) => ({ ...prev, [key]: value }));
  };

  const handlePenaltyChange = (key: string, checked: boolean) => {
    setEditablePenalties((prev) => ({ ...prev, [key]: checked }));
  };

  const handleSave = async (group: GroupData) => {
    if (isSaving) return; // 保存中は二重実行しない
    setIsSaving(true);
    setSaveError(null);

    try {
      const groupRef = doc(db, "groups", group.id);
      const updates: { [key: string]: number | boolean | Timestamp } = {};

      Object.entries(editableResults).forEach(([key, value]) => {
        const num = Number(value);
        updates[key] = isNaN(num) ? 0 : num;
      });

      Object.entries(editablePenalties).forEach(([key, value]) => {
        updates[key] = value;
      });

      updates.updatedAt = Timestamp.now();

      await updateDoc(groupRef, updates);

      // 変更した1件だけをローカルの状態に反映する（groups全件の再取得を避ける）
      setGroups((prev) =>
        prev.map((g) => (g.id === group.id ? { ...g, ...updates } : g)),
      );

      setEditGroupId(null);
    } catch (error) {
      console.error("保存失敗:", error);
      setSaveError("保存に失敗しました。");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredGroups = groups.filter((group) => {
    if (group.entryClass !== selectedTab) return false;
    const createdAt = group.createdAt?.toDate();
    return createdAt?.getFullYear() === currentYear;
  });

  let sortedGroups: GroupData[];

  // order順ソート
  if (selectedStage === "Final" && sortOption === "entryNumber") {
    const scoreSorted = getSortedGroups(filteredGroups, {}, false, [1, 2]);

    const currentResultPage = resultPages.find(
      (page) => Number(page.year) === currentYear,
    );

    const finalistCount = currentResultPage?.finalistCount ?? 0;
    const finalists = scoreSorted.slice(0, finalistCount);

    sortedGroups = [...finalists].sort(
      (a, b) => (a.finalOrder ?? Infinity) - (b.finalOrder ?? Infinity),
    );
  } else {
    sortedGroups = [...filteredGroups].sort((a, b) => {
      switch (sortOption) {
        case "entryNumber":
          return (Number(a.entryNumber) || 0) - (Number(b.entryNumber) || 0);
        case "createdAt":
          return (
            (a.createdAt?.toDate().getTime() ?? 0) -
            (b.createdAt?.toDate().getTime() ?? 0)
          );
        case "groupName":
          return (a.groupName ?? "").localeCompare(b.groupName ?? "", "ja");
        default:
          return 0;
      }
    });
  }

  const getSongIndexesForStage = (stage: StageOption): number[][] => {
    if (selectedTab !== "QuartetContest") {
      return [[1, 2]];
    }

    if (stage === "SemiFinal") return [[1, 2]];
    if (stage === "Final") return [[3, 4]];
    return [
      [1, 2],
      [3, 4],
    ]; // All の場合は2ブロックに分けて返す
  };

  //リザルトページ操作モーダル
  const currentResultPage = resultPages.find(
    (page) => page.year === String(currentYear),
  );

  const isCreated = !!currentResultPage;

  return (
    <div className="table-contents">
      <div className="resultpage-setting">
        <button
          className={`resultpage-btn ${
            isCreated ? "resultpage-btn-manage" : "resultpage-btn-create"
          }`}
          onClick={() => {
            setResultPageModalMode(isCreated ? "manage" : "create");
            setResultPageModalOpen(true);
          }}
        >
          <span className="button-item">
            <Settings size="16" /> <span>Results page</span>
          </span>
        </button>
      </div>
      {resultPageModalOpen && (
        <ResultPageSettingModal
          open={resultPageModalOpen}
          mode={resultPageModalMode}
          year={currentYear}
          groups={groups}
          onClose={() => setResultPageModalOpen(false)}
        />
      )}
      <div className="table-main">
        <div className="table-header">
          <YearPager
            currentYear={currentYear}
            minYear={2024}
            maxYear={new Date().getFullYear()}
            setCurrentYear={setCurrentYear}
          />
          <div className="flex">
            {selectedTab === "QuartetContest" && (
              <div className="sub-btn" onClick={() => setModalOpen(true)}>
                Final Order
              </div>
            )}
            {selectedTab === "QuartetContest" && (
              <StageSelect
                selectedStage={selectedStage}
                setSelectedStage={setSelectedStage}
              />
            )}
            <SortSelect
              sortOption={sortOption}
              setSortOption={setSortOption}
              options={[
                { value: "entryNumber", label: "エントリーNo.順" },
                { value: "groupName", label: "グループ名順" },
                { value: "createdAt", label: "応募日付順" },
              ]}
            />

            {/* FinalOrderManager モーダル */}
            <OrderModal
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              currentYear={currentYear}
              type="final"
              selectedTab={selectedTab}
              onSaved={fetchGroups}
            />
          </div>
        </div>
        {sortedGroups.map((group) => {
          const isEditMode = editGroupId === group.id;
          const stageSections = getSongIndexesForStage(selectedStage);

          return (
            <section className="card-list" key={group.id}>
              <article className="list-card">
                <div className="flex-space-between">
                  <div className="flex-align-center">
                    {selectedStage === "Final" ? (
                      <p className="entry-number">{group.finalOrder}</p>
                    ) : (
                      <p className="entry-number">{group.entryNumber}</p>
                    )}
                    <h2>{group.groupName}</h2>
                  </div>
                  <div>
                    <p className="updated-at">
                      {group.updatedAt
                        ? group.updatedAt.toDate().toLocaleDateString("ja-JP", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                        : "未設定"}
                    </p>
                    <div className="list-edit">
                      <button
                        className={`editbtn 
              ${isEditMode ? "editmode" : ""} 
              ${isSaving ? "saving" : ""}`}
                        onClick={() =>
                          isEditMode ? handleSave(group) : handleEdit(group)
                        }
                        disabled={isSaving} // 保存中は押せない
                      >
                        {isSaving
                          ? "保存中..."
                          : isEditMode
                            ? "入力完了"
                            : "入力開始"}
                      </button>
                      {saveError && <p className="error">{saveError}</p>}
                    </div>
                  </div>
                </div>
                <div className="result-area">
                  <table className="result-table result-total-ave-table">
                    <thead>
                      <tr>
                        <th>
                          <span className="total-point">
                            {getTotalBySongs(
                              group,
                              editableResults,
                              isEditMode,
                              [1, 2, 3, 4],
                            )}
                          </span>
                        </th>
                        <th style={{ paddingLeft: "20px" }}></th>
                        <th className="center">
                          <p>Mus</p>
                          <p className="th-ave">
                            {getAverageBySongs(
                              group,
                              editableResults,
                              isEditMode,
                              [1, 2, 3, 4],
                              ["Mus"],
                            )}
                          </p>
                        </th>
                        <th className="center">
                          <p>Per</p>
                          <p className="th-ave">
                            {getAverageBySongs(
                              group,
                              editableResults,
                              isEditMode,
                              [1, 2, 3, 4],
                              ["Per"],
                            )}
                          </p>
                        </th>
                        <th className="center">
                          <p>Sng</p>
                          <p className="th-ave">
                            {getAverageBySongs(
                              group,
                              editableResults,
                              isEditMode,
                              [1, 2, 3, 4],
                              ["Sng"],
                            )}
                          </p>
                        </th>
                        <th className="center">
                          <p>Total</p>
                          <p className="th-ave">
                            {getAverageBySongs(
                              group,
                              editableResults,
                              isEditMode,
                              [1, 2, 3, 4],
                            )}
                          </p>
                        </th>
                      </tr>
                    </thead>
                  </table>
                  {stageSections
                    .filter((indexes) =>
                      selectedTab !== "QuartetContest"
                        ? indexes[0] !== 3 && indexes[0] !== 4
                        : true,
                    )
                    .reverse()
                    .map((indexes, i) => (
                      <div key={i}>
                        <ResultTable
                          group={group}
                          songIndexes={indexes}
                          editableResults={editableResults}
                          editablePenalties={editablePenalties}
                          isEditMode={isEditMode}
                          handleChange={handleChange}
                          handlePenaltyChange={handlePenaltyChange}
                          selectedTab={selectedTab}
                          isSaving={isSaving}
                        />
                      </div>
                    ))}
                </div>
              </article>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default ResultLists;
